/* eslint-disable default-param-last */
import {
  QUESTIONS_REQUEST,
  QUESTIONS_SUCCESS,
  QUESTIONS_APPEND,
  QUESTIONS_LOADING_MORE,
  QUESTIONS_FAIL,
  VOTE_SUCCESS,
  COMMENTS_REQUEST,
  COMMENTS_SUCCESS,
  COMMENTS_FAIL,
  COMMENT_CREATE_SUCCESS,
  COMMENT_LIKE_SUCCESS,
  COMMENT_REPLY_SUCCESS,
  COMMENTS_RESET,
  QUESTION_SINGLE_SUCCESS,
  QUESTION_DELETE_SUCCESS,
  COMMENT_DELETE_SUCCESS,
  QUESTION_PATCH,
} from '../types/game';
import { LOGOUT, LOGIN_SUCCESS, REGISTER_SUCCESS } from '../types/auth';

const FEED_INITIAL = { questions: [], loading: false, loadingMore: false, page: 1, hasMore: true, seed: null, activeRequestId: null, lastDeletion: null };

export const questionsReducer = (state = FEED_INITIAL, action) => {
  switch (action.type) {
    case QUESTIONS_REQUEST:
      return {
        ...state,
        loading: true,
        loadingMore: false,
        page: 1,
        hasMore: true,
        seed: null,
        // Latest fetch wins: SUCCESS/FAIL for any earlier request is stale.
        activeRequestId: action.meta?.requestId ?? null,
      };
    case QUESTIONS_SUCCESS: {
      // Drop stale responses (an earlier category's slow fetch landing after
      // a newer one) - accepting them unioned two categories into one feed
      // and reset pagination. Tokenless legacy dispatches (tests) pass.
      if (action.meta?.requestId && state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
        return state;
      }
      const payloadQuestions = Array.isArray(action.payload) ? action.payload : action.payload.questions;
      const seed = Array.isArray(action.payload) ? null : action.payload.seed;
      const existingIds = new Set(payloadQuestions.map((q) => q.id));
      const keep = state.questions.filter((q) => !existingIds.has(q.id));
      return {
        ...state,
        loading: false,
        error: null,
        questions: [...keep, ...payloadQuestions],
        page: 1,
        hasMore: payloadQuestions.length >= 10,
        seed,
      };
    }
    case QUESTIONS_APPEND: {
      // Drop appends from a superseded feed generation: a slow page-2
      // response landing after a fresh load's SUCCESS (loading already
      // false) would inject old-fetch rows and clobber seed/page/hasMore.
      // Any mismatch drops - including against a null activeRequestId,
      // which means LOGOUT/LOGIN/REGISTER reset the feed while this page
      // was in flight (its rows carry the old identity's user_vote flags).
      // Tokenless legacy dispatches (tests) still pass.
      if (action.meta?.requestId && action.meta.requestId !== state.activeRequestId) {
        return { ...state, loadingMore: false };
      }
      // Ignore stale appends if a fresh feed load is in progress
      if (state.loading) return { ...state, loadingMore: false };
      const payloadQuestions = Array.isArray(action.payload) ? action.payload : action.payload.questions;
      const seed = Array.isArray(action.payload) ? state.seed : action.payload.seed;
      const existingIds = new Set(state.questions.map((q) => q.id));
      const newQuestions = payloadQuestions.filter((q) => !existingIds.has(q.id));
      const combined = [...state.questions, ...newQuestions];
      return {
        ...state,
        loadingMore: false,
        questions: combined.length > 200 ? combined.slice(-200) : combined,
        page: state.page + 1,
        hasMore: payloadQuestions.length >= 10,
        seed,
      };
    }
    case QUESTIONS_LOADING_MORE:
      return { ...state, loadingMore: true };
    case QUESTIONS_FAIL: {
      if (action.meta?.requestId && state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
        return state;
      }
      return { ...state, loading: false, loadingMore: false, error: action.payload };
    }
    case VOTE_SUCCESS:
      return {
        ...state,
        // Merge instead of replace: the vote endpoint's shape can lag the
        // feed's (feed-only fields like is_trending would be wiped by a
        // whole-object swap). Server fields win on conflict.
        questions: state.questions.map((q) => (q.id === action.payload.id ? { ...q, ...action.payload } : q)),
      };
    case QUESTION_SINGLE_SUCCESS: {
      const exists = state.questions.some((q) => q.id === action.payload.id);
      return {
        ...state,
        loading: false,
        questions: exists
          ? state.questions.map((q) => (q.id === action.payload.id ? action.payload : q))
          : [action.payload, ...state.questions],
      };
    }
    case QUESTION_DELETE_SUCCESS:
      return {
        ...state,
        questions: state.questions.filter((q) => q.id !== action.payload),
        // Explicit deletion signal for cache consumers (DashboardApp): a
        // monotonic seq so the same id deleted twice still retriggers.
        lastDeletion: { id: action.payload, seq: (state.lastDeletion?.seq || 0) + 1 },
      };
    case QUESTION_PATCH:
      return {
        ...state,
        questions: state.questions.map((q) => (
          q.id === action.payload.id ? { ...q, ...action.payload.patch } : q
        )),
      };
    case LOGOUT:
      return FEED_INITIAL;
    // Identity changed: cached questions were fetched with the OLD identity's
    // Authorization header, so their user_vote/is_bookmarked flags are wrong
    // for the new one. Without this, votes adopted at registration didn't
    // show as voted until a manual reload.
    case LOGIN_SUCCESS:
    case REGISTER_SUCCESS:
      return FEED_INITIAL;
    default:
      return state;
  }
};

export const commentsReducer = (state = { comments: [], loading: false }, action) => {
  switch (action.type) {
    case COMMENTS_RESET:
    case LOGOUT:
      return { comments: [], loading: false };
    case COMMENTS_REQUEST:
      return { ...state, loading: true };
    case COMMENTS_SUCCESS:
      return { loading: false, comments: action.payload };
    case COMMENTS_FAIL:
      return { loading: false, error: action.payload, comments: [] };
    case COMMENT_CREATE_SUCCESS:
      return { ...state, comments: [...state.comments, action.payload] };
    case COMMENT_REPLY_SUCCESS: {
      const { parentId, reply } = action.payload;
      return {
        ...state,
        comments: state.comments.map((c) =>
          c.id === parentId ? { ...c, replies: [...(c.replies || []), reply] } : c
        ),
      };
    }
    case COMMENT_LIKE_SUCCESS: {
      const { commentId, parentId: pId, liked, likes_count: likesCount } = action.payload;
      if (pId) {
        return {
          ...state,
          comments: state.comments.map((c) =>
            c.id === pId
              ? {
                  ...c,
                  replies: (c.replies || []).map((r) =>
                    r.id === commentId ? { ...r, is_liked: liked, likes_count: likesCount } : r
                  ),
                }
              : c
          ),
        };
      }
      return {
        ...state,
        comments: state.comments.map((c) =>
          c.id === commentId ? { ...c, is_liked: liked, likes_count: likesCount } : c
        ),
      };
    }
    case COMMENT_DELETE_SUCCESS: {
      const { commentId: delId, parentId: delParentId } = action.payload;
      if (delParentId) {
        return {
          ...state,
          comments: state.comments.map((c) =>
            c.id === delParentId
              ? { ...c, replies: (c.replies || []).filter((r) => r.id !== delId) }
              : c
          ),
        };
      }
      return {
        ...state,
        comments: state.comments.filter((c) => c.id !== delId),
      };
    }
    default:
      return state;
  }
};
